import type { JsonSchema7, UISchemaElement } from '@jsonforms/core';
import type { PropertyFilterProps } from '@risk-smart/themed-cloudscape-components';
import type { CustomAttributeFieldType } from '@risksmart-app/form-configuration/src/field-types/types';
import type { FormFieldOption } from '@risksmart-app/form-configuration/src/types';
import type { ModuleConfig } from '@risksmart-app/modules/src/index';
import type {
  FieldRegistryLookup,
  FormId,
} from '@risksmart-app/shared/forms/formConfigRegistry';
import { getFormConfigRegistry } from '@risksmart-app/shared/forms/formConfigRegistry';
import type { FormFieldConfig } from '@risksmart-app/shared/forms/types';
import type { GetStandardFormFieldLabel } from 'src/hooks/forms/useFormCustomisation';
import { displayTypes } from 'src/pages/custom-datasources/update/display-types';
import type {
  AdditionalData,
  Helpers,
} from 'src/pages/custom-datasources/update/display-types/types';
import { getCustomAttributeRenderProps } from 'src/utils/table/utils/customAttributes';

import { fieldTypesConfig } from '../custom-attributes/field-types';
import type { FieldRendererProps } from '../custom-attributes/renderers/collection-layouts/types';
import {
  FREE_TEXT_CONDITION_OPERATORS,
  freeTextOperatorDefaults,
} from './conditionOperators';

/**
 * A condition-source field that exists only in the form editor's current
 * session (not yet persisted to the custom-attribute schema or the standard
 * form registry). The form-config preview supplies these so the evaluator can
 * resolve a rule whose `propertyKey` is the new field's editor id.
 */
export interface AdditionalConditionField {
  /**
   * The key the rule's `propertyKey` and the preview Controller `name` both
   * use — the editor temp id.
   */
  key: string;
  type: CustomAttributeFieldType;
  label: string;
  /** Selectable options for option-based field types (e.g. select). */
  options?: { id: string; label: string }[];
}

const additionalFieldToRenderProps = (
  field: AdditionalConditionField
): FieldRendererProps => ({
  type: field.type,
  label: field.label,
  scope: field.key,
  path: field.key,
  options: field.options?.map(
    (opt): FormFieldOption => ({
      _tag: 'AltValueOption',
      AltValue: opt.id,
      Value: opt.label,
      GeneratedId: crypto.randomUUID(),
    })
  ),
});

export const getFormFieldConfig = (
  { formId, fieldId }: FieldRegistryLookup,
  formSectionsEnabled: boolean,
  modules: ModuleConfig
): FormFieldConfig | undefined => {
  const formRegistry = getFormConfigRegistry(formSectionsEnabled, modules);

  return formRegistry[formId as keyof typeof formRegistry]?.[
    fieldId as keyof (typeof formRegistry)[FormId]
  ] as FormFieldConfig | undefined;
};

export const getConditionalPropertyFilterProps = ({
  formId,
  schema,
  uiSchema,
  data,
  helpers,
  formSectionsEnabled,
  modules,
  excludedFieldIds,
  additionalFields,
  getStandardFieldLabel,
}: {
  formId: FormId;
  excludedFieldIds?: string[];
  schema: JsonSchema7 | undefined;
  uiSchema: UISchemaElement | undefined;
  data: AdditionalData;
  helpers: Helpers;
  formSectionsEnabled: boolean;
  modules: ModuleConfig;
  /**
   * Session-only condition-source fields (e.g. form-editor preview), appended
   * to the persisted custom + standard properties. Omit for live forms.
   */
  additionalFields?: AdditionalConditionField[];
  getStandardFieldLabel: GetStandardFormFieldLabel;
}): Pick<PropertyFilterProps, 'filteringProperties' | 'filteringOptions'> => {
  const allCustomFields =
    schema && uiSchema ? getCustomAttributeRenderProps(schema, uiSchema) : [];
  const allowedCustomFields = allCustomFields.filter(
    (cf) => fieldTypesConfig[cf.type].allowAsConditionSource
  );

  const formRegistry = getFormConfigRegistry(formSectionsEnabled, modules);
  const form = formRegistry[formId] ?? {};
  const allowedStandardFields = Object.values<FormFieldConfig>(form).filter(
    (field) => field.allowAsConditionSource
  );

  const customFieldProperties = allowedCustomFields.map((rp) => {
    const fieldConfig = fieldTypesConfig[rp.type];
    const properties = fieldConfig.getConditionalPropertyFilterProperty?.(
      rp,
      data
    );

    return {
      key: `CustomAttributeData.${rp.path}`,
      groupValuesLabel: 'Custom fields',
      propertyLabel: rp.label,
      ...properties,
      ...freeTextOperatorDefaults(rp.type, properties),
    };
  });

  const standardFieldProperties = allowedStandardFields.map((field) => {
    const propertyLabel = getStandardFieldLabel(formId, field.fieldId as never);
    if (field.displayType) {
      const displayType = displayTypes[field.displayType!.displayType];
      const property = displayType.propertyConfig?.(
        {
          ...field.displayType!,
          key: field.fieldId,
          groupValuesLabel: '',
          propertyLabel,
        },
        helpers,
        data
      ) ?? {
        key: field.fieldId,
        groupValuesLabel: '',
        propertyLabel,
      };

      // Defensive: both free-text displayTypes already declare all four today.
      return {
        ...property,
        ...freeTextOperatorDefaults(field.displayType.displayType, property),
      };
    }

    // No displayType → free-text capture (e.g. Risk `Title`).
    return {
      key: field.fieldId,
      groupValuesLabel: '',
      propertyLabel,
      operators: [...FREE_TEXT_CONDITION_OPERATORS],
    };
  });

  const customFieldFilteringOptions = allowedCustomFields.flatMap((cf) => {
    const fieldConfig = fieldTypesConfig[cf.type];

    const filteringOptions = fieldConfig.getConditionalPropertyFilterOptions?.(
      cf,
      data
    );

    return (
      filteringOptions?.map((fo) => ({
        ...fo,
        propertyKey: `CustomAttributeData.${cf.path}`,
      })) ?? []
    );
  });

  const baseProperties = [...standardFieldProperties, ...customFieldProperties];
  const baseKeys = new Set(baseProperties.map((fp) => fp.key));

  const additionalFieldProperties: typeof customFieldProperties = [];
  const additionalFieldFilteringOptions: typeof customFieldFilteringOptions =
    [];

  for (const field of additionalFields ?? []) {
    if (baseKeys.has(field.key)) {
      continue;
    }

    const fieldConfig = fieldTypesConfig[field.type];
    const renderProps = additionalFieldToRenderProps(field);
    const properties = fieldConfig.getConditionalPropertyFilterProperty?.(
      renderProps,
      data
    );

    additionalFieldProperties.push({
      key: field.key,
      groupValuesLabel: 'New fields',
      propertyLabel: field.label,
      ...properties,
      ...freeTextOperatorDefaults(field.type, properties),
    });

    const filteringOptions = fieldConfig.getConditionalPropertyFilterOptions?.(
      renderProps,
      data
    );
    for (const fo of filteringOptions ?? []) {
      additionalFieldFilteringOptions.push({ ...fo, propertyKey: field.key });
    }
  }

  return {
    filteringProperties: [
      ...baseProperties,
      ...additionalFieldProperties,
    ].filter((fp) => !excludedFieldIds?.includes(fp.key)),
    filteringOptions: [
      ...customFieldFilteringOptions,
      ...additionalFieldFilteringOptions,
    ].filter((fo) => !excludedFieldIds?.includes(fo.propertyKey)),
  };
};
